import SecurityOutlined from '@mui/icons-material/SecurityOutlined'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { useEffect, useMemo, useState } from 'react'
import {
  useCreateRole,
  useDeleteRole,
  usePermissionCatalogue,
  useRoles,
  useSetRolePermissions,
  useUpdateRole,
  type AdminRole,
} from '../../api/admin'
import { describeError } from '../../api/client'
import { useAuth } from '../../auth/useAuth'
import { CrudTable, type CrudField } from '../../components/CrudTable'
import { PageHeader } from '../../components/PageHeader'
import { ActiveChip } from '../../components/StatusChip'
import {
  SCREENS,
  SCREEN_SECTIONS,
  permissionsFor,
  type AccessLevel,
} from '../../nav/screens'

const SCOPES = [
  { value: 'ALL', label: 'Everything' },
  { value: 'COMPANY', label: 'Whole company' },
  { value: 'PROJECT', label: 'Assigned projects' },
  { value: 'SITE', label: 'Assigned sites' },
  { value: 'OWN', label: 'Own records only' },
]

/**
 * Roles and what each one grants.
 *
 * <p>The eight built-in roles are shared by every company and are shown read-only: a
 * company that needs something different copies one into a role of its own rather than
 * changing what "Site Engineer" means for everyone.</p>
 */
export function RolesPage() {
  const { can } = useAuth()
  const roles = useRoles()
  const create = useCreateRole()
  const update = useUpdateRole()
  const remove = useDeleteRole()

  const [granting, setGranting] = useState<AdminRole | null>(null)

  const canCreate = can('ROLE:CREATE')
  const canUpdate = can('ROLE:UPDATE')
  const canDelete = can('ROLE:DELETE')

  const fields: CrudField[] = [
    {
      name: 'code',
      label: 'Code',
      required: true,
      lockedOnEdit: true,
      helperText: 'Letters, digits and underscore, e.g. QA_INSPECTOR',
    },
    { name: 'name', label: 'Role name', required: true },
    { name: 'description', label: 'Description', type: 'multiline' },
    {
      name: 'defaultScope',
      label: 'Default data scope',
      type: 'select',
      required: true,
      defaultValue: 'SITE',
      options: SCOPES,
      helperText: 'Suggested reach for people given this role',
    },
    { name: 'active', label: 'Active', type: 'switch', defaultValue: true },
  ]

  return (
    <>
      <PageHeader
        title="Roles and permissions"
        subtitle="What each role may do. Built-in roles are fixed; create your own to grant anything else."
      />

      <Paper variant="outlined">
        <CrudTable
          query={roles}
          rows={roles.data ?? []}
          addLabel="Add role"
          describeRow={(r) => r.name}
          fields={fields}
          columns={[
            {
              label: 'Role',
              render: (r) => (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {r.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {r.code}
                  </Typography>
                </Box>
              ),
            },
            { label: 'Description', render: (r) => r.description ?? '-' },
            {
              label: 'Origin',
              render: (r) =>
                r.system ? (
                  <Chip size="small" label="Built-in" sx={{ fontWeight: 600 }} />
                ) : (
                  <Chip size="small" color="primary" variant="outlined" label="Your company" />
                ),
            },
            { label: 'Default scope', render: (r) => SCOPES.find((s) => s.value === r.defaultScope)?.label ?? r.defaultScope },
            { label: 'Permissions', render: (r) => r.permissionCodes.length, align: 'right' },
            { label: 'Users', render: (r) => r.userCount, align: 'right' },
            { label: 'Active', render: (r) => <ActiveChip active={r.active} /> },
          ]}
          onCreate={
            canCreate
              ? (values) =>
                  create.mutateAsync({
                    code: String(values.code ?? '').trim(),
                    name: String(values.name ?? '').trim(),
                    description: values.description ? String(values.description) : undefined,
                    defaultScope: values.defaultScope as AdminRole['defaultScope'],
                    active: Boolean(values.active),
                    permissionCodes: [],
                  })
              : undefined
          }
          onUpdate={
            canUpdate
              ? (row, values) =>
                  update.mutateAsync({
                    id: row.id,
                    body: {
                      code: row.code,
                      name: String(values.name ?? '').trim(),
                      description: values.description ? String(values.description) : undefined,
                      defaultScope: values.defaultScope as AdminRole['defaultScope'],
                      active: Boolean(values.active),
                    },
                  })
              : undefined
          }
          onDelete={canDelete ? (row) => remove.mutateAsync(row.id) : undefined}
          deleteNote="A role still held by someone cannot be deleted. Move those people first."
          rowActions={(r) => (
            <Tooltip title={r.system ? 'View what this role grants' : 'Edit what this role grants'}>
              <IconButton size="small" onClick={() => setGranting(r)}>
                <SecurityOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        />
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Changing what a role grants immediately ends the sessions of everyone holding it,
        so a revoked permission stops working at once.
      </Typography>

      {granting && <PermissionDialog role={granting} onClose={() => setGranting(null)} />}
    </>
  )
}

/**
 * Granting a role by the screens it can reach.
 *
 * <p>The underlying model is 134 permissions across 31 modules, which is the right
 * model for the server and the wrong one for a person: nobody decides that a foreman
 * should hold MATERIAL_RECEIPT:INSPECT. They decide he can open Material received. So
 * the side menu is the unit of granting, and the permissions follow from it.</p>
 *
 * <p>A module can sit behind more than one screen — material receipts are reachable
 * from the entry screen and from a daily report — so the grant is recomputed as the
 * union across every screen each time, rather than toggled per screen. Turning one
 * screen off therefore cannot revoke something another screen still needs.</p>
 */
function PermissionDialog({ role, onClose }: { role: AdminRole; onClose: () => void }) {
  const { can } = useAuth()
  const catalogue = usePermissionCatalogue()
  const setPermissions = useSetRolePermissions()
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const editable = !role.system && can('ROLE:UPDATE')

  /** module -> every permission code it contains. */
  const byModule = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const m of catalogue.data ?? []) {
      map.set(m.module, m.permissions.map((p) => p.code))
    }
    return map
  }, [catalogue.data])

  const grantable = useMemo(() => SCREENS.filter((s) => s.modules.length > 0), [])

  /**
   * Permissions this editor does not govern, kept exactly as they are.
   *
   * <p>Several modules — the audit trail, notifications, sync — have no screen in the
   * menu yet. Recomputing the grant from screens alone would quietly strip them from a
   * role that already held them, so they are carried through untouched.</p>
   */
  const untouched = useMemo(() => {
    if (!catalogue.data) return []
    const governed = new Set(
      grantable.flatMap((s) => permissionsFor(s, 'full', byModule)),
    )
    return role.permissionCodes.filter((c) => !governed.has(c))
  }, [catalogue.data, grantable, byModule, role.permissionCodes])

  /** Best fit of the role's existing grant onto each screen. */
  const initialLevels = useMemo(() => {
    const held = new Set(role.permissionCodes)
    const levels: Record<string, AccessLevel> = {}
    for (const screen of grantable) {
      const full = permissionsFor(screen, 'full', byModule)
      const view = permissionsFor(screen, 'view', byModule)
      levels[screen.to] =
        full.length > 0 && full.every((c) => held.has(c))
          ? 'full'
          : view.length > 0 && view.every((c) => held.has(c))
            ? 'view'
            : 'none'
    }
    return levels
  }, [role.permissionCodes, grantable, byModule])

  const [levels, setLevels] = useState<Record<string, AccessLevel>>({})
  const ready = catalogue.data != null
  // The catalogue arrives after the first render, so the fitted levels land then.
  useEffect(() => {
    if (ready) setLevels(initialLevels)
  }, [ready, initialLevels])

  const selected = useMemo(() => {
    const codes = new Set<string>(untouched)
    for (const screen of grantable) {
      for (const c of permissionsFor(screen, levels[screen.to] ?? 'none', byModule)) {
        codes.add(c)
      }
    }
    return [...codes]
  }, [levels, grantable, byModule, untouched])

  function setAll(level: AccessLevel) {
    setLevels(Object.fromEntries(grantable.map((s) => [s.to, level])))
  }

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await setPermissions.mutateAsync({ id: role.id, permissionCodes: selected })
      onClose()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {role.name}
        <Typography variant="body2" color="text.secondary">
          What this role can open in the side menu — {selected.length} permission
          {selected.length === 1 ? '' : 's'} in total
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {Boolean(error) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {describeError(error)}
          </Alert>
        )}
        {role.system && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This is a built-in role, shared by every company, so it is shown read-only.
            Create a role of your own to grant a different set.
          </Alert>
        )}
        {!ready && <Typography color="text.secondary">Loading the catalogue...</Typography>}

        {ready && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>View</strong> lets the role read that data wherever it appears.{' '}
              <strong>Full</strong> adds creating, editing, deleting and approving, and is
              what makes the menu link itself appear for the daily entry screens.
            </Alert>

            {editable && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Button size="small" onClick={() => setAll('none')}>Clear all</Button>
                <Button size="small" onClick={() => setAll('view')}>All view-only</Button>
                <Button size="small" onClick={() => setAll('full')}>All full</Button>
              </Box>
            )}

            {SCREEN_SECTIONS.map((section) => {
              const rows = grantable.filter((s) => s.section === section)
              if (rows.length === 0) return null
              return (
                <Box key={section} sx={{ mb: 2.5 }}>
                  <Typography
                    variant="overline"
                    sx={{ color: 'text.secondary', letterSpacing: 1 }}
                  >
                    {section}
                  </Typography>
                  {rows.map((screen) => (
                    <Box
                      key={screen.to}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        py: 1,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 220 }}>
                        <Box sx={{ color: 'text.secondary', display: 'flex' }}>{screen.icon}</Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {screen.label}
                          </Typography>
                          {screen.hint && (
                            <Typography variant="caption" color="text.secondary">
                              {screen.hint}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        color="primary"
                        disabled={!editable}
                        value={levels[screen.to] ?? 'none'}
                        onChange={(_, v: AccessLevel | null) => {
                          if (v) setLevels((prev) => ({ ...prev, [screen.to]: v }))
                        }}
                      >
                        <ToggleButton value="none">None</ToggleButton>
                        <ToggleButton value="view">View</ToggleButton>
                        <ToggleButton value="full">Full</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  ))}
                </Box>
              )
            })}

            {untouched.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                This role also holds <strong>{untouched.length}</strong> permission
                {untouched.length === 1 ? '' : 's'} for parts of the system that have no
                menu link yet, such as the audit trail and notifications. They are kept
                exactly as they are and are not affected by anything above.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          {editable ? 'Cancel' : 'Close'}
        </Button>
        {editable && (
          <Button variant="contained" onClick={() => void save()} disabled={busy || !ready}>
            Save grants
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
